import PasskeysAPI from '../api/PasskeysAPI';
import { createAction, createRequestTypes } from './utils';
import { arePasskeysSupported } from '../utils/arePasskeysSupported';
import I18n from 'I18n';
import { trackPasskeyNudgeDismissed, trackPasskeyNudgeSetupCompleted, trackPasskeyNudgeSetupFailed, trackPasskeyNudgeUserCancelled, trackPasskeySettingsSetupCompleted, trackPasskeySettingsSetupFailed, trackPasskeySettingsUserCancelled } from '../lib/tracker';
import { redirect } from '../utils/redirect';
import Raven from 'raven-js';
export const TOGGLE_PASSKEY_DELETE_MODAL = 'TOGGLE_PASSKEY_DELETE_MODAL';
export const TOGGLE_PASSKEY_RENAME_MODAL = 'TOGGLE_PASSKEY_RENAME_MODAL';
export const REGISTER_PASSKEY = 'REGISTER_PASSKEY';
export const VALIDATE_PASSKEY = 'VALIDATE_PASSKEY';
export const FETCH_PASSKEYS = 'FETCH_PASSKEYS';
export const DELETE_PASSKEY = 'DELETE_PASSKEY';
export const RegisterPasskeyActionTypes = createRequestTypes(REGISTER_PASSKEY);
export const ValidatePasskeyActionTypes = createRequestTypes(VALIDATE_PASSKEY);
export const FetchPasskeysActionTypes = createRequestTypes(FETCH_PASSKEYS);
export const DeletePasskeyActionTypes = createRequestTypes(DELETE_PASSKEY);
export const RenamePasskeyActionTypes = createRequestTypes('RENAME_PASSKEY');
export const CREATE_PASSKEY_CREDENTIAL_FAILURE = 'CREATE_PASSKEY_CREDENTIAL_FAILURE';
export const SETUP_PASSKEY_FROM_NUDGE = 'SETUP_PASSKEY_FROM_NUDGE';
export const SKIP_PASSKEY_SETUP_NUDGE = 'SKIP_PASSKEY_SETUP_NUDGE';
export const AAGUID_ALREADY_EXISTS = 'AAGUID_ALREADY_EXISTS_FAILURE';
export const CREATE_PASSKEY_SUCCESS = 'CREATE_PASSKEY_SUCCESS';
export const togglePasskeyDeleteModal = () => createAction(TOGGLE_PASSKEY_DELETE_MODAL);
export const togglePasskeyRenameModal = () => createAction(TOGGLE_PASSKEY_RENAME_MODAL);
export const createCredential = async response => {
  // Transform the public key to the expected data types expected by navigator.credentials.create
  const publicKey = Object.assign({}, response, {
    challenge: Uint8Array.from(response.challenge, letter => letter.charCodeAt(0)),
    user: Object.assign({}, response.user, {
      id: Uint8Array.from(response.user.id, letter => letter.charCodeAt(0))
    })
  });
  const passkeysSupported = await arePasskeysSupported();
  if (passkeysSupported) {
    return await navigator.credentials.create({
      publicKey
    });
  } else {
    throw new Error(I18n.text('userPreferences.passkeys.disabledReason'));
  }
};
export const createPasskey = (email, fromNudge = false, redirectUrl) => dispatch => {
  dispatch({
    type: RegisterPasskeyActionTypes.REQUEST
  });
  return PasskeysAPI.registerPasskey(email).then(registrationResponse => {
    dispatch({
      type: RegisterPasskeyActionTypes.SUCCESS
    });
    return createCredential(registrationResponse).then(credential => {
      if (!credential) {
        throw new Error('The public key credential could not be created.');
      }
      dispatch({
        type: ValidatePasskeyActionTypes.REQUEST
      });
      return PasskeysAPI.validatePasskey(registrationResponse, credential).then(passkey => {
        dispatch({
          type: ValidatePasskeyActionTypes.SUCCESS,
          payload: passkey
        });
        if (fromNudge) {
          dispatch({
            type: CREATE_PASSKEY_SUCCESS,
            payload: passkey
          });
          trackPasskeyNudgeSetupCompleted();
          if (redirectUrl) {
            // Show success message briefly before redirecting
            setTimeout(() => redirect(redirectUrl), 1000);
          }
        } else {
          trackPasskeySettingsSetupCompleted();
        }
      }).catch(error => {
        const parsedError = JSON.parse(error.data);
        if ((parsedError === null || parsedError === void 0 ? void 0 : parsedError.status) === 'AAGUID_ALREADY_EXISTS') {
          Raven.captureException(error, {
            extra: {
              actionType: AAGUID_ALREADY_EXISTS
            }
          });
          dispatch({
            type: AAGUID_ALREADY_EXISTS,
            payload: {
              error
            }
          });
        } else {
          Raven.captureException(error, {
            extra: {
              actionType: ValidatePasskeyActionTypes.FAILURE
            }
          });
          dispatch({
            type: ValidatePasskeyActionTypes.FAILURE,
            payload: {
              error
            }
          });
        }
        if (fromNudge) {
          trackPasskeyNudgeSetupFailed();
        } else {
          trackPasskeySettingsSetupFailed();
        }
      });
    }).catch(error => {
      if (error.name === 'NotAllowedError') {
        if (fromNudge) {
          trackPasskeyNudgeUserCancelled();
        } else {
          trackPasskeySettingsUserCancelled();
        }
      } else {
        Raven.captureException(error, {
          extra: {
            actionType: CREATE_PASSKEY_CREDENTIAL_FAILURE
          }
        });
        if (fromNudge) {
          trackPasskeyNudgeSetupFailed();
        } else {
          trackPasskeySettingsSetupFailed();
        }
      }
      dispatch({
        type: CREATE_PASSKEY_CREDENTIAL_FAILURE,
        payload: {
          error
        }
      });
    });
  }).catch(error => {
    Raven.captureException(error, {
      extra: {
        actionType: RegisterPasskeyActionTypes.FAILURE
      }
    });
    dispatch({
      type: RegisterPasskeyActionTypes.FAILURE,
      payload: {
        error
      }
    });
    if (fromNudge) {
      trackPasskeyNudgeSetupFailed();
    } else {
      trackPasskeySettingsSetupFailed();
    }
  });
};
export const fetchPasskeys = userId => dispatch => {
  dispatch({
    type: FetchPasskeysActionTypes.REQUEST
  });
  PasskeysAPI.fetchPasskeys(userId).then(response => {
    dispatch({
      type: FetchPasskeysActionTypes.SUCCESS,
      payload: response
    });
  }).catch(error => {
    dispatch({
      type: FetchPasskeysActionTypes.FAILURE,
      payload: error
    });
  });
};
export const deletePasskey = credentialId => dispatch => {
  dispatch({
    type: DeletePasskeyActionTypes.REQUEST
  });
  PasskeysAPI.deletePasskey(credentialId).then(() => {
    dispatch({
      type: DeletePasskeyActionTypes.SUCCESS,
      payload: credentialId
    });
  }).catch(error => {
    dispatch({
      type: DeletePasskeyActionTypes.FAILURE,
      payload: error
    });
  });
};
export const renamePasskey = (credentialId, name) => dispatch => {
  dispatch({
    type: RenamePasskeyActionTypes.REQUEST
  });
  PasskeysAPI.renamePasskey(credentialId, name).then(() => dispatch({
    type: RenamePasskeyActionTypes.SUCCESS,
    payload: {
      credentialId,
      name
    }
  })).catch(error => {
    dispatch({
      type: RenamePasskeyActionTypes.FAILURE,
      payload: error
    });
  });
};
export const setupPasskeyFromNudge = redirectUrl => {
  return dispatch => {
    dispatch({
      type: SETUP_PASSKEY_FROM_NUDGE,
      payload: {
        redirectUrl
      }
    });
  };
};
export const skipPasskeySetupNudge = redirectUrl => dispatch => {
  dispatch({
    type: SKIP_PASSKEY_SETUP_NUDGE
  });
  trackPasskeyNudgeDismissed();
  if (redirectUrl) {
    redirect(redirectUrl);
  }
};